'use client';

import { motion, useReducedMotion } from 'motion/react';
import { useId, useState } from 'react';

import { OutboundTextLink } from '@/components/patterns/OutboundTextLink';
import { durations, easeStandard } from '@/motion/tokens';
import underlineHoverStyles from '@/styles/underlineHover.module.css';

import styles from './OverlayResourceDisclosure.module.css';

import type { OverlayResourceLink } from '@/content/data/overlay-entries';

const MOTION_EXPAND = { duration: durations.base + 0.1, ease: easeStandard };

function triggerClassName(isExpanded: boolean): string {
  return [
    styles.trigger,
    underlineHoverStyles.root,
    isExpanded ? styles.triggerActive : null,
    isExpanded ? underlineHoverStyles.revealed : null,
  ]
    .filter(Boolean)
    .join(' ');
}

type OverlayResourceDisclosureProps = {
  articles?: readonly OverlayResourceLink[];
  caseStudies?: readonly OverlayResourceLink[];
  label: string;
};

/** Combines an overlay's article and case-study links under one disclosure. */
export function OverlayResourceDisclosure({
  articles,
  caseStudies,
  label,
}: OverlayResourceDisclosureProps) {
  const listId = useId();
  const reducedMotion = useReducedMotion();
  const [isExpanded, setIsExpanded] = useState(false);
  const links = [...(articles ?? []), ...(caseStudies ?? [])];

  if (links.length === 0) {
    return null;
  }

  const transition = reducedMotion ? { duration: 0 } : MOTION_EXPAND;

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={triggerClassName(isExpanded)}
        aria-expanded={isExpanded}
        aria-controls={listId}
        onClick={() => setIsExpanded((expanded) => !expanded)}
      >
        {label}
      </button>

      <motion.div
        className={styles.panel}
        initial={false}
        animate={
          isExpanded
            ? { height: 'auto', opacity: 1 }
            : { height: 0, opacity: 0 }
        }
        transition={transition}
        aria-hidden={!isExpanded}
        inert={!isExpanded ? true : undefined}
      >
        <ul id={listId} className={styles.list}>
          {links.map((link, index) => (
            <li key={`${link.href}-${link.label}-${index}`} className={styles.item}>
              <OutboundTextLink href={link.href} className={styles.link}>
                {link.label}
              </OutboundTextLink>
            </li>
          ))}
        </ul>
      </motion.div>
    </div>
  );
}
